import { useEffect, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { Reveal } from "../components/Motion";
import { useSiteSettings, ArtistName } from "../context/SiteSettingsContext";

const FALLBACK_PORTRAIT = "https://static.prod-images.emergentagent.com/jobs/9f155735-a3e2-49f2-ade7-372db22ee705/images/d911c9a6c9e50920f578ad3faf9143762eaa7dbc97b72a475910e2ac7ee187df.jpeg";

/* Editorial copy comes ONLY from the CMS Artist Profile. Never display invented
   philosophy, chronology or lore here. */
export default function Artist() {
  const ref = useRef(null);
  const { profile } = useSiteSettings();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  useEffect(() => {
    document.title = `The Artist — ${profile.displayName || "Motion & Meaning"}`;
  }, [profile.displayName]);

  const portrait = profile.aboutImage || profile.profileImage || FALLBACK_PORTRAIT;

  return (
    <div data-testid="artist-page">
      <section ref={ref} className="relative h-[100svh] overflow-hidden">
        <motion.img style={{ y, scale: 1.1 }} src={portrait} alt={`${profile.displayName || "Motion & Meaning"}, the artist`}
          className="w-full h-full object-cover object-top" data-testid="artist-portrait" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/20 to-[#050505]/50" />
        <div className="absolute bottom-0 left-0 right-0 max-w-[1440px] mx-auto px-6 md:px-12 pb-16">
          <Reveal>
            <p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-5">THE ARTIST</p>
            <h1 className="font-serif text-6xl sm:text-8xl lg:text-[10rem] font-light leading-[0.85] text-white">
              <ArtistName name={profile.displayName} />
            </h1>
            {profile.tagline && (
              <p className="font-mono text-[11px] tracking-[0.35em] text-white/60 mt-6" data-testid="artist-tagline">
                {profile.tagline.toUpperCase()}
              </p>
            )}
          </Reveal>
        </div>
      </section>

      {profile.aboutBio && (
        <section className="py-24 md:py-32 bg-[#050505]" data-testid="artist-bio-section">
          <div className="max-w-[1440px] mx-auto px-6 md:px-12">
            <Reveal>
              <p className="font-mono text-[10px] tracking-[0.4em] text-gold mb-8">ABOUT</p>
              <div className="font-serif text-2xl md:text-3xl font-light leading-relaxed text-white/85 whitespace-pre-line max-w-3xl">
                {profile.aboutBio}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex flex-wrap items-center gap-5">
              <Link to="/music" data-testid="artist-to-music"
                className="bg-gold text-black font-mono text-[11px] tracking-[0.25em] px-10 py-4 hover:bg-white transition-colors duration-300">
                THE MUSIC
              </Link>
              <Link to="/dimension-i" data-testid="artist-to-dimension-i"
                className="border border-white/25 text-white/80 font-mono text-[11px] tracking-[0.25em] px-10 py-4 hover:border-gold hover:text-gold transition-colors duration-300">
                ENTER DIMENSION I
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
